import React from "react";
import { useNavigate } from "react-router-dom";

import { useGetMyCardsQuery } from "../api/respectCardsApi";
import type { CardInstance, OwnedCardTile } from "../types/cardTypes";
import { comparePlayableFirst, isInHand } from "../lib/cardState";
import CardTile from "./CardTile";
import "../styles/handStrip.css";

/** Max tiles before the "view all" tail takes over. */
const MAX_TILES = 8;

export interface HandStripProps {
  /**
   * Restrict the row to cards whose effect `scope` is in this list. Used by the
   * AOTD panel (P5) to show only the AOTD-relevant hand. Omit to show the whole
   * hand.
   */
  scopeFilter?: string[];
  /**
   * Play handler. When provided each tile renders a Play button; the parent
   * resolves `tile.playableInstanceId` and opens the play modal. Omit for a
   * read-only strip (tap a tile to expand; "view all" still routes to /cards).
   */
  onPlay?: (tile: OwnedCardTile) => void;
  /** Reserved for a denser AOTD layout; the tiles are already compact (xs). */
  compact?: boolean;
}

/**
 * Horizontal scroll-snap row of the in-hand cards, playable-first then soonest
 * expiry, capped at 8 with a "view all" tail into `/cards`. Fetches `/my-cards`
 * only while mounted — the caller (home / AOTD panel) gates the mount on
 * `handCount>0` from the cheap hand-summary, so an empty hand never triggers
 * the full fetch. Self-hides if the (filtered) hand turns out empty.
 */
export default function HandStrip({ scopeFilter, onPlay }: HandStripProps) {
  const navigate = useNavigate();
  const { data } = useGetMyCardsQuery();

  const scopeSet = React.useMemo(
    () => (scopeFilter && scopeFilter.length > 0 ? new Set(scopeFilter) : null),
    [scopeFilter],
  );

  const handTiles = React.useMemo(() => {
    const tiles = (data?.tiles ?? []).filter(isInHand);
    const scoped = scopeSet
      ? tiles.filter((t) => {
          const scope = t.definition?.effect?.scope;
          return scope ? scopeSet.has(scope) : false;
        })
      : tiles;
    return scoped.slice().sort(comparePlayableFirst);
  }, [data?.tiles, scopeSet]);

  // Per-card in_hand instances so a multi-copy tile can enumerate its expiries.
  const handInstancesByCardId = React.useMemo(() => {
    const m = new Map<string, CardInstance[]>();
    for (const c of data?.cards ?? []) {
      if (c.status !== "in_hand") continue;
      const arr = m.get(c.cardId) ?? [];
      arr.push(c);
      m.set(c.cardId, arr);
    }
    return m;
  }, [data?.cards]);

  if (handTiles.length === 0) return null;

  const visible = handTiles.slice(0, MAX_TILES);
  const overflow = handTiles.length - visible.length;

  return (
    <section className="hand-strip" aria-label="Your hand">
      <div className="hand-strip__head">
        <span className="hand-strip__title">Your hand</span>
        <span className="hand-strip__count">
          {handTiles.length} {handTiles.length === 1 ? "card" : "cards"}
        </span>
      </div>
      <div className="hand-strip__row">
        {visible.map((tile) => {
          // A too_late card stays visible but greyed — the user learns the
          // play window existed (CardTile's button reads "Too late today").
          const tooLate =
            tile.playability?.state === "blocked" &&
            tile.playability.code === "too_late";
          return (
            <div
              className={`hand-strip__item${tooLate ? " hand-strip__item--too-late" : ""}`}
              key={tile.cardId}
            >
              <CardTile
                tile={tile}
                handInstances={handInstancesByCardId.get(tile.cardId)}
                onPlay={onPlay}
                interactive={Boolean(onPlay)}
              />
            </div>
          );
        })}
        <button
          type="button"
          className="hand-strip__tail"
          onClick={() => navigate("/cards")}
        >
          {overflow > 0 ? `View all · +${overflow} more` : "View all cards"}
        </button>
      </div>
    </section>
  );
}
